/**
 * Vera (AI chat) constants — web UI.
 *
 * Keep this file in sync with fides-capital-native-app/src/constants/vera.ts
 * and fides-capital-native-app/src/screens/VeraScreen.tsx.
 *
 * Visibility contexts:
 *   'eoi'    — shown when user is in an EOI / fund opportunity context
 *   'global' — shown in the global floating Vera chat (all pages)
 */

export type VeraQuestionContext = 'eoi' | 'global';

export interface VeraSuggestedQuestion {
  id: string;
  label: string;
  /** Which contexts show this question. */
  context: VeraQuestionContext[];
}

/**
 * Starter prompts for the global Vera chat panel.
 * Must match STARTER_PROMPTS in fides-capital-native-app/src/screens/VeraScreen.tsx.
 */
export const VERA_GLOBAL_STARTER_PROMPTS = [
  "What's changed in my portfolio over the last 30 days?",
  'Tell me about upcoming opportunities?',
  'Do I have any capital movements coming up?',
];

/**
 * Prefabricated questions for the EOI / fund opportunity context.
 * Must match VERA_SUGGESTED_QUESTIONS in fides-capital-native-app/src/constants/vera.ts.
 * Used by FloatingVeraButton (EOI view) in the app; used by FundOpportunityChat in the web.
 */
export const VERA_SUGGESTED_QUESTIONS: VeraSuggestedQuestion[] = [
  { id: 'min-investment', label: 'What is the minimum investment?', context: ['eoi', 'global'] },
  { id: 'how-submit-eoi', label: 'How do I submit an EOI?', context: ['eoi'] },
  { id: 'documents-needed', label: 'What documents do I need?', context: ['eoi', 'global'] },
  { id: 'withdraw-eoi', label: 'How do I withdraw my EOI?', context: ['eoi'] },
  { id: 'timeline', label: 'What is the typical timeline?', context: ['eoi', 'global'] },
  { id: 'contact', label: 'Who can I contact for support?', context: ['global'] },
];

/** Return EOI suggested questions visible for the given context. */
export function getVisibleSuggestedQuestions(
  context: VeraQuestionContext,
): VeraSuggestedQuestion[] {
  return VERA_SUGGESTED_QUESTIONS.filter((q) => q.context.includes(context));
}

/**
 * User-facing privacy note — keep in sync with fides-capital-native-app/src/constants/vera.ts
 * and all Vera chat surfaces (disclaimers, footers, terms).
 */
export const VERA_PRIVACY_NOTE =
  "Your conversations stay private. We don't use your data to train AI models, and nothing you share with Vera is shown to other users.";

/** Shorter variant for compact footers and inline notices. */
export const VERA_PRIVACY_NOTE_SHORT =
  "Your data stays private — we don't train on your conversations, and nothing you share is shown to other users.";
